import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/** @typedef {'idle' | 'adding' | 'success' | 'checkout'} StickyAtcButtonState */

/** @type {string} */
const HC_BRAND_PINK = '#e03192';

/** @type {number} */
const MIN_ADDING_MS = 450;

/** @type {number} */
const SUCCESS_DISPLAY_MS = 2800;

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLElement} addedMessage - Success/error toast above the sticky bar
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} [checkoutLabel] - Checkout label shown after add
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * Injects sticky ATC animation styles once (ships with JS — reliable on live storefront).
 */
function ensureStickyAtcStyles() {
  if (document.getElementById('hc-sticky-atc-animation-styles')) return;

  const style = document.createElement('style');
  style.id = 'hc-sticky-atc-animation-styles';
  style.textContent = `
    .hc-sticky-atc .sticky-add-to-cart__button.is-atc-adding {
      pointer-events: none;
    }

    .hc-sticky-atc .sticky-add-to-cart__button.is-atc-success {
      background: #fff !important;
      color: ${HC_BRAND_PINK} !important;
      border-color: ${HC_BRAND_PINK} !important;
      box-shadow: 3px 3px 0 0 ${HC_BRAND_PINK} !important;
    }

    .hc-sticky-atc .sticky-add-to-cart__button.is-atc-success .add-to-cart-text,
    .hc-sticky-atc .sticky-add-to-cart__button.is-atc-success .add-to-cart__added {
      opacity: 0 !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }

    .hc-sticky-atc .sticky-add-to-cart__button.is-atc-success .sticky-atc-btn__success {
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      gap: 6px;
      position: absolute;
      inset: 0;
      padding: 0 16px;
      font-weight: 600;
      color: ${HC_BRAND_PINK};
    }

    .hc-sticky-atc .sticky-add-to-cart__button.is-atc-success .sticky-atc-btn__success svg {
      stroke: ${HC_BRAND_PINK};
      flex-shrink: 0;
    }

    .hc-sticky-atc .sticky-add-to-cart__message.is-visible {
      opacity: 1 !important;
      transform: translateX(-50%) translateY(0) !important;
    }

    .hc-sticky-atc .sticky-add-to-cart__button.is-atc-checkout .hc-sticky-atc__cart-icon {
      display: none !important;
    }

    .hc-sticky-atc .sticky-add-to-cart__button.is-atc-checkout .add-to-cart-text,
    .hc-sticky-atc .sticky-add-to-cart__button.is-atc-checkout .add-to-cart__added,
    .hc-sticky-atc .sticky-add-to-cart__button.is-atc-checkout .sticky-atc-btn__success {
      display: none !important;
    }

    .hc-sticky-atc .sticky-add-to-cart__button.is-atc-checkout .sticky-atc-btn__checkout-label {
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      color: #fff;
    }
  `;
  document.head.appendChild(style);
}

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber', 'addedMessage'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #pendingStickyAdd = false;

  /** @type {number | undefined} */
  #messageTimeout;

  /** @type {number | undefined} */
  #addFallbackTimer;

  /** @type {number | undefined} */
  #successFallbackTimer;

  /** @type {number | undefined} */
  #checkoutTransitionTimeout;

  /** @type {HTMLElement | null} */
  #productForm = null;

  /** @type {Animation | null} */
  #buttonAnimation = null;

  /** @type {StickyAtcButtonState} */
  #buttonState = 'idle';

  /** @type {boolean} */
  #isCheckoutMode = false;

  /** @type {number} */
  #addStartedAt = 0;

  /** @type {() => void} */
  #handleMobileScroll = () => {
    if (!this.#isMobileViewport()) return;
    if (this.dataset.variantAvailable !== 'true') return;
    if (this.#hiddenByBottom) return;

    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = scrollHeight > 0 ? window.scrollY / scrollHeight : 0;

    if (progress >= 0.3) {
      this.#showStickyBar();
      return;
    }

    const productForm = this.#getProductForm();
    const buyButtonsBlock = productForm?.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const rect = buyButtonsBlock.getBoundingClientRect();
    const buyButtonsInView = rect.top < window.innerHeight && rect.bottom > 0;
    if (buyButtonsInView) {
      this.#hideStickyBar();
    }
  };

  connectedCallback() {
    super.connectedCallback();

    ensureStickyAtcStyles();

    this.#setupIntersectionObserver();
    this.#setupMobileScrollTrigger();
    this.#bindProductFormListeners();
    requestAnimationFrame(() => this.#bindProductFormListeners());

    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    this.#cancelButtonAnimation();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#messageTimeout) {
      clearTimeout(this.#messageTimeout);
    }
    if (this.#addFallbackTimer) {
      clearTimeout(this.#addFallbackTimer);
    }
    if (this.#successFallbackTimer) {
      clearTimeout(this.#successFallbackTimer);
    }
    if (this.#checkoutTransitionTimeout) {
      clearTimeout(this.#checkoutTransitionTimeout);
    }
  }

  /**
   * Binds cart listeners on the PDP product form (events dispatch from this element, not always document).
   */
  #bindProductFormListeners() {
    const productForm = this.#getProductForm();
    if (!productForm || productForm === this.#productForm) return;

    this.#productForm = productForm;
    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    section?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    productForm.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    productForm.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });

    const mainButton = productForm.querySelector('[ref="addToCartButton"]');
    if (mainButton instanceof HTMLButtonElement) {
      mainButton.addEventListener('click', this.#handleMainAtcClick, { capture: true, signal });
    }
  }

  /**
   * Clears the pending-add fallback timer.
   */
  #clearAddFallbackTimer() {
    if (this.#addFallbackTimer) {
      clearTimeout(this.#addFallbackTimer);
      this.#addFallbackTimer = undefined;
    }
  }

  /**
   * Clears the success fallback timer.
   */
  #clearSuccessFallbackTimer() {
    if (this.#successFallbackTimer) {
      clearTimeout(this.#successFallbackTimer);
      this.#successFallbackTimer = undefined;
    }
  }

  /**
   * Opens the cart drawer after a successful add.
   */
  #openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  /**
   * Clears the checkout transition timer.
   */
  #clearCheckoutTransitionTimer() {
    if (this.#checkoutTransitionTimeout) {
      clearTimeout(this.#checkoutTransitionTimeout);
      this.#checkoutTransitionTimeout = undefined;
    }
  }

  /**
   * Resets the button if cart feedback never arrives (network / app interference).
   */
  #startAddFallbackTimer() {
    this.#clearAddFallbackTimer();
    this.#addFallbackTimer = setTimeout(() => {
      if (!this.#pendingStickyAdd) return;
      this.#pendingStickyAdd = false;
      this.#clearSuccessFallbackTimer();
      this.#openCartDrawer();
      this.#setButtonState('idle');
    }, 10000);
  }

  /**
   * Shows success if cart event is swallowed by a third-party app.
   */
  #startSuccessFallbackTimer() {
    this.#clearSuccessFallbackTimer();
    this.#successFallbackTimer = setTimeout(() => {
      if (!this.#pendingStickyAdd && !this.#addStartedAt) return;
      this.#pendingStickyAdd = false;
      this.#clearAddFallbackTimer();
      this.#showCartFeedback(false);
    }, 2200);
  }

  /**
   * Stops any in-flight Web Animations API pulse/pop.
   */
  #cancelButtonAnimation() {
    this.#buttonAnimation?.cancel();
    this.#buttonAnimation = null;
  }

  /**
   * Runs a visible pulse while the cart request is in flight.
   * @param {HTMLButtonElement} button
   */
  #runAddingAnimation(button) {
    if (typeof button.animate !== 'function') return;

    this.#buttonAnimation = button.animate(
      [
        { transform: 'scale(1)', opacity: '1' },
        { transform: 'scale(0.9)', opacity: '0.82' },
        { transform: 'scale(1)', opacity: '1' },
      ],
      {
        duration: 650,
        iterations: Infinity,
        easing: 'ease-in-out',
      }
    );
  }

  /**
   * Runs a success pop on the sticky button.
   * @param {HTMLButtonElement} button
   */
  #runSuccessAnimation(button) {
    if (typeof button.animate !== 'function') return;

    this.#buttonAnimation = button.animate(
      [
        { transform: 'scale(0.88)' },
        { transform: 'scale(1.08)' },
        { transform: 'scale(1)' },
      ],
      {
        duration: 550,
        easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
        fill: 'forwards',
      }
    );
  }

  /**
   * Sets the sticky button visual state (idle / adding / success / checkout).
   * Uses Web Animations API so feedback works even when theme CSS is cached or overridden.
   * @param {StickyAtcButtonState} state
   */
  #setButtonState(state) {
    const { addToCartButton } = this.refs;
    this.#buttonState = state;
    this.#cancelButtonAnimation();

    addToCartButton.removeAttribute('data-atc-adding');
    addToCartButton.removeAttribute('data-atc-success');
    addToCartButton.removeAttribute('data-added');
    addToCartButton.removeAttribute('data-atc-mode');
    addToCartButton.classList.remove('is-atc-adding', 'is-atc-success', 'is-atc-checkout');

    const successLabel = addToCartButton.querySelector('.sticky-atc-btn__success');
    if (successLabel instanceof HTMLElement) {
      successLabel.hidden = true;
    }

    if (state === 'idle') {
      addToCartButton.style.removeProperty('transform');
      this.#restoreAddToCartLabel();
      this.#updateButtonText();
      return;
    }

    if (state === 'adding') {
      addToCartButton.dataset.atcAdding = 'true';
      addToCartButton.classList.add('is-atc-adding');
      this.#restoreAddToCartLabel();
      this.#runAddingAnimation(addToCartButton);
      return;
    }

    if (state === 'checkout') {
      addToCartButton.dataset.atcMode = 'checkout';
      addToCartButton.classList.add('is-atc-checkout');
      addToCartButton.style.removeProperty('transform');
      this.#setCheckoutLabel();
      return;
    }

    addToCartButton.dataset.atcSuccess = 'true';
    addToCartButton.classList.add('is-atc-success');
    if (successLabel instanceof HTMLElement) {
      successLabel.hidden = false;
    }
    this.#runSuccessAnimation(addToCartButton);
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getButtonLabelElement() {
    return this.refs.addToCartButton.querySelector('.sticky-atc-btn__label');
  }

  /**
   * @returns {string}
   */
  #getAddToCartLabelText() {
    return this.dataset.addToCartLabel || Theme?.translations?.add_to_cart || 'Add to cart';
  }

  /**
   * @returns {string}
   */
  #getCheckoutLabelText() {
    return this.dataset.checkoutLabel || Theme?.translations?.checkout || 'Check out';
  }

  /**
   * @returns {string}
   */
  #getCheckoutUrl() {
    return this.dataset.checkoutUrl || '/checkout';
  }

  /**
   * Restores the default add-to-cart label and cart icon.
   */
  #restoreAddToCartLabel() {
    const { addToCartButton, checkoutLabel } = this.refs;
    const addText = addToCartButton.querySelector('.add-to-cart-text');
    const checkoutEl =
      checkoutLabel instanceof HTMLElement
        ? checkoutLabel
        : addToCartButton.querySelector('.sticky-atc-btn__checkout-label');

    if (addText instanceof HTMLElement) {
      addText.hidden = false;
    }

    if (checkoutEl instanceof HTMLElement) {
      checkoutEl.hidden = true;
    }

    const label = this.#getButtonLabelElement();
    if (label) {
      label.textContent = this.#getAddToCartLabelText();
    }

    const cartIcon = addToCartButton.querySelector('.hc-sticky-atc__cart-icon');
    if (cartIcon instanceof HTMLElement) {
      cartIcon.hidden = false;
    }
  }

  /**
   * Switches the sticky button label to checkout.
   */
  #setCheckoutLabel() {
    const { addToCartButton, checkoutLabel } = this.refs;
    const addText = addToCartButton.querySelector('.add-to-cart-text');
    const successLabel = addToCartButton.querySelector('.sticky-atc-btn__success');
    const checkoutEl =
      checkoutLabel instanceof HTMLElement
        ? checkoutLabel
        : addToCartButton.querySelector('.sticky-atc-btn__checkout-label');

    if (addText instanceof HTMLElement) {
      addText.hidden = true;
    }

    if (successLabel instanceof HTMLElement) {
      successLabel.hidden = true;
    }

    if (checkoutEl instanceof HTMLElement) {
      checkoutEl.hidden = false;
      checkoutEl.textContent = this.#getCheckoutLabelText();
      return;
    }

    const label = this.#getButtonLabelElement();
    if (label) {
      label.textContent = this.#getCheckoutLabelText();
    }

    if (addText instanceof HTMLElement) {
      addText.hidden = false;
    }

    const cartIcon = addToCartButton.querySelector('.hc-sticky-atc__cart-icon');
    if (cartIcon instanceof HTMLElement) {
      cartIcon.hidden = true;
    }

    this.refs.quantityDisplay.style.display = 'none';
  }

  /**
   * Updates the main PDP add-to-cart button to match checkout mode.
   * @param {boolean} isCheckout
   */
  #syncMainButtonCheckoutMode(isCheckout) {
    const mainButton = this.#resolveTargetAddToCartButton();
    if (!mainButton) return;

    const label =
      mainButton.querySelector('.add-to-cart-text__content span span') ??
      mainButton.querySelector('.add-to-cart-text__content');

    if (isCheckout) {
      mainButton.dataset.hcAtcMode = 'checkout';
      if (label instanceof HTMLElement) {
        if (!mainButton.dataset.hcOriginalLabel) {
          mainButton.dataset.hcOriginalLabel =
            label.textContent?.trim() || this.#getAddToCartLabelText();
        }
        label.textContent = this.#getCheckoutLabelText();
      }
      return;
    }

    mainButton.removeAttribute('data-hc-atc-mode');
    if (label instanceof HTMLElement && mainButton.dataset.hcOriginalLabel) {
      label.textContent = mainButton.dataset.hcOriginalLabel;
    }
    delete mainButton.dataset.hcOriginalLabel;
  }

  /**
   * Switches sticky + main PDP buttons to checkout mode.
   * @param {boolean} [immediate=false]
   */
  #enterCheckoutMode = async (immediate = false) => {
    if (this.#isCheckoutMode) return;

    if (!immediate && this.#buttonState === 'adding') {
      const elapsed = Date.now() - this.#addStartedAt;
      if (elapsed < MIN_ADDING_MS) {
        await new Promise((resolve) => setTimeout(resolve, MIN_ADDING_MS - elapsed));
      }

      this.#setButtonState('success');
      await new Promise((resolve) => setTimeout(resolve, 400));
    }

    this.#pendingStickyAdd = false;
    this.#isCheckoutMode = true;
    this.dataset.buttonMode = 'checkout';
    this.#setButtonState('checkout');
    this.#syncMainButtonCheckoutMode(true);
  };

  /**
   * Restores sticky + main buttons to add-to-cart mode.
   */
  #exitCheckoutMode() {
    this.#isCheckoutMode = false;
    this.#addStartedAt = 0;
    this.#pendingStickyAdd = false;
    delete this.dataset.buttonMode;
    this.#clearCheckoutTransitionTimer();
    this.#setButtonState('idle');
    this.#syncMainButtonCheckoutMode(false);
  };

  /**
   * Redirects to checkout when the main PDP button is clicked in checkout mode.
   * @param {Event} event
   */
  #handleMainAtcClick = (event) => {
    if (!this.#isCheckoutMode) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(this.#getCheckoutUrl());
  };

  /**
   * @returns {boolean}
   */
  #isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * Shows sticky bar after 30% page scroll on mobile (Meta cold-traffic PDP behavior).
   */
  #setupMobileScrollTrigger() {
    const { signal } = this.#abortController;
    window.addEventListener('scroll', this.#handleMobileScroll, { passive: true, signal });
    requestAnimationFrame(this.#handleMobileScroll);
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#bindProductFormListeners();
  }

  /**
   * Resolves the main PDP add-to-cart button at click time.
   * @returns {HTMLButtonElement | null}
   */
  #resolveTargetAddToCartButton() {
    if (this.#targetAddToCartButton?.isConnected) return this.#targetAddToCartButton;

    const productForm = this.#getProductForm();
    if (!productForm) return null;

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    return this.#targetAddToCartButton;
  }

  /**
   * Submits the main product form from the sticky bar.
   * @param {HTMLButtonElement} targetButton
   */
  #submitMainProductForm(targetButton) {
    const productForm = this.#getProductForm();
    const form = productForm?.querySelector('form');

    targetButton.dataset.puppet = 'true';

    if (form && typeof form.requestSubmit === 'function') {
      form.requestSubmit(targetButton);
      return;
    }

    targetButton.click();
  }

  // Public action handlers
  /**
   * Handles the sticky bar button click (add to cart or checkout).
   */
  handleAddToCartClick = async () => {
    const { addToCartButton } = this.refs;
    if (addToCartButton.disabled) return;

    if (this.#isCheckoutMode || this.dataset.buttonMode === 'checkout') {
      window.location.assign(this.#getCheckoutUrl());
      return;
    }

    if (this.#buttonState === 'adding' || this.#buttonState === 'success') return;

    const targetButton = this.#resolveTargetAddToCartButton();
    if (!targetButton) return;

    this.#pendingStickyAdd = true;
    this.#addStartedAt = Date.now();
    this.#setButtonState('adding');
    this.#startAddFallbackTimer();
    this.#startSuccessFallbackTimer();

    this.#submitMainProductForm(targetButton);

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (cartIcon && this.refs.productImage) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = this.refs.productImage;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);
      await onAnimationEnd(flyToCartElement);
    }
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    const eventProductId = event.detail.data.productId;
    if (
      eventProductId &&
      String(eventProductId) !== String(this.dataset.productId)
    ) {
      return;
    }

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
      this.#bindProductFormListeners();
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#pendingStickyAdd = false;
    this.#clearAddFallbackTimer();
    this.#clearSuccessFallbackTimer();
    this.#clearCheckoutTransitionTimer();
    this.#exitCheckoutMode();
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Finishes the sticky add flow with optional minimum adding duration.
   * @param {boolean} isError
   */
  #completeStickyAdd = async (isError = false) => {
    const elapsed = Date.now() - this.#addStartedAt;
    if (!isError && this.#buttonState === 'adding' && elapsed < MIN_ADDING_MS) {
      await new Promise((resolve) => setTimeout(resolve, MIN_ADDING_MS - elapsed));
    }

    this.#showCartFeedback(isError);
  };

  /**
   * Shows success/error feedback on the sticky button and toast.
   * @param {boolean} [isError=false]
   */
  #showCartFeedback = (isError = false) => {
    const { addedMessage } = this.refs;
    const successText = Theme?.translations?.added_to_cart || 'Added to cart';
    const errorText = Theme?.translations?.add_to_cart_error || 'Error adding to cart';
    const message = isError ? errorText : successText;

    if (addedMessage) {
      addedMessage.textContent = message;
      addedMessage.classList.toggle('sticky-add-to-cart__message--error', isError);
      addedMessage.hidden = false;
      addedMessage.classList.add('is-visible');
    }

    if (this.#messageTimeout) clearTimeout(this.#messageTimeout);
    this.#clearCheckoutTransitionTimer();

    if (isError) {
      this.#exitCheckoutMode();
      this.#messageTimeout = setTimeout(() => {
        if (addedMessage) {
          addedMessage.hidden = true;
          addedMessage.classList.remove('sticky-add-to-cart__message--error', 'is-visible');
        }
      }, 3000);
      return;
    }

    this.#messageTimeout = setTimeout(() => {
      if (addedMessage) {
        addedMessage.hidden = true;
        addedMessage.classList.remove('sticky-add-to-cart__message--error', 'is-visible');
      }
      this.#setButtonState('idle');
    }, SUCCESS_DISPLAY_MS);

    this.#exitCheckoutMode();
    this.#openCartDrawer();
    if (this.#buttonState === 'adding') {
      this.#setButtonState('success');
    }
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag and shows feedback
   * @param {CustomEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    const targetButton = this.#resolveTargetAddToCartButton();
    if (targetButton) {
      targetButton.dataset.puppet = 'false';
    }

    const productForm = this.#productForm ?? this.#getProductForm();
    const fromThisProductForm =
      event.detail?.data?.source === 'product-form-component' &&
      productForm instanceof HTMLElement &&
      (event.target === productForm || event.composedPath?.().includes(productForm));

    const stickyAddInFlight =
      this.#pendingStickyAdd ||
      (this.#addStartedAt > 0 && Date.now() - this.#addStartedAt < 10000);

    if (!fromThisProductForm && !stickyAddInFlight) return;

    this.#pendingStickyAdd = false;
    this.#clearAddFallbackTimer();
    this.#clearSuccessFallbackTimer();
    this.#clearCheckoutTransitionTimer();

    const isError = event.type === ThemeEvents.cartError || event.detail?.data?.didError === true;

    if (isError) {
      if (stickyAddInFlight || this.#buttonState === 'adding') {
        this.#completeStickyAdd(true);
      }
      return;
    }

    this.#completeStickyAdd(false);
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;

    if (this.#isCheckoutMode) {
      this.#exitCheckoutMode();
    }

    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    if (this.#isCheckoutMode) return;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
